import { z } from "zod";

// Validação do snapshot estrutural do cenário (extraída de scenarios.ts p/ manter
// o arquivo de schemas ≤200 LOC). Tipos estruturais mínimos evitam import circular dos schemas.
interface StructureShape {
  machines: Array<{ key: string; present: boolean; sourceMachineId: string; stageKey: string }>;
  stages: Array<{ key: string; order: number }>;
  edges: Array<{ sourceKey: string; targetKey: string }>;
  canvas: { nodes: Array<{ key: string; nodeType: "MACHINE" | "START" | "END" }> };
}

// Detecção de ciclo em PRODUCTION_FLOW (DFS com pilha de recursão) sobre keys de nós.
function hasCycle(edges: Array<{ sourceKey: string; targetKey: string }>): boolean {
  const adj = new Map<string, string[]>();
  for (const e of edges) {
    const list = adj.get(e.sourceKey) ?? [];
    list.push(e.targetKey);
    adj.set(e.sourceKey, list);
  }
  const WHITE = 0,
    GRAY = 1,
    BLACK = 2;
  const color = new Map<string, number>();
  const visit = (node: string): boolean => {
    color.set(node, GRAY);
    for (const next of adj.get(node) ?? []) {
      const c = color.get(next) ?? WHITE;
      if (c === GRAY) return true;
      if (c === WHITE && visit(next)) return true;
    }
    color.set(node, BLACK);
    return false;
  };
  for (const node of adj.keys()) {
    if ((color.get(node) ?? WHITE) === WHITE && visit(node)) return true;
  }
  return false;
}

export function refineScenarioStructure(data: StructureShape, ctx: z.RefinementCtx): void {
  const starts = data.canvas.nodes.filter((n) => n.nodeType === "START").length;
  const ends = data.canvas.nodes.filter((n) => n.nodeType === "END").length;
  if (starts !== 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `O cenário deve conter exatamente 1 nó de início (encontrado: ${starts}).`,
      path: ["canvas", "nodes"],
    });
  }
  if (ends !== 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `O cenário deve conter exatamente 1 nó de fim (encontrado: ${ends}).`,
      path: ["canvas", "nodes"],
    });
  }
  const nodeKeys = new Set(data.canvas.nodes.map((n) => n.key));
  for (const e of data.edges) {
    if (!nodeKeys.has(e.sourceKey) || !nodeKeys.has(e.targetKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Conexão referencia nó inexistente (${e.sourceKey} → ${e.targetKey}).`,
        path: ["edges"],
      });
    }
  }
  const machineKeys = data.machines.map((m) => m.key);
  if (new Set(machineKeys).size !== machineKeys.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Chaves de máquina duplicadas no cenário.",
      path: ["machines"],
    });
  }
  const presentSources = data.machines.filter((m) => m.present).map((m) => m.sourceMachineId);
  if (new Set(presentSources).size !== presentSources.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Uma mesma máquina não pode aparecer duas vezes como presente no cenário.",
      path: ["machines"],
    });
  }
  if (hasCycle(data.edges)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "O fluxo de produção do cenário não pode conter ciclos.",
      path: ["edges"],
    });
  }
  // Estágios: cada máquina referencia um estágio existente; keys/ordens únicas.
  const stageKeys = new Set(data.stages.map((s) => s.key));
  for (const m of data.machines) {
    if (!stageKeys.has(m.stageKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Máquina ${m.key} referencia estágio inexistente (${m.stageKey}).`,
        path: ["machines"],
      });
    }
  }
  const stageKeyList = data.stages.map((s) => s.key);
  if (new Set(stageKeyList).size !== stageKeyList.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Chaves de estágio duplicadas no cenário.",
      path: ["stages"],
    });
  }
  const orders = data.stages.map((s) => s.order);
  if (new Set(orders).size !== orders.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Ordens de estágio duplicadas no cenário.",
      path: ["stages"],
    });
  }
}
